import React, { useState, useEffect } from 'react';
import userService from '../../services/userService';
import businessService from '../../services/businessService';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Save as SaveIcon
} from '@mui/icons-material';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface Business {
  id: number;
  name: string;
  industry: string;
  address: string;
  active: boolean;
}

const UserAssignment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [allBusinesses, setAllBusinesses] = useState<Business[]>([]);
  const [assignedBusinessIds, setAssignedBusinessIds] = useState<Set<number>>(new Set());
  const [initialAssignedIds, setInitialAssignedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [userRes, assignedRes, allBusinessesRes] = await Promise.all([
        userService.getById(Number(id)),
        userService.getAssignedBusinesses(Number(id)),
        businessService.list({ active: true, limit: 1000 })
      ]);

      setUser(userRes);

      // Si el usuario es administrador, no permitir asignación
      if (userRes.role === 'admin') {
        setError('Los administradores tienen acceso automático a todos los negocios');
        setLoading(false);
        return;
      }
      
      const assigned = new Set<number>(assignedRes.businesses?.map((b: Business) => b.id) || []);
      setAssignedBusinessIds(assigned);
      setInitialAssignedIds(new Set<number>(assigned));
      
      setAllBusinesses(allBusinessesRes.data || []);

    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBusiness = (businessId: number) => {
    setAssignedBusinessIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(businessId)) {
        newSet.delete(businessId);
      } else {
        newSet.add(businessId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Determinar qué negocios asignar y cuáles quitar
      const toAssign = Array.from(assignedBusinessIds).filter(
        id => !initialAssignedIds.has(id)
      );
      const toUnassign = Array.from(initialAssignedIds).filter(
        id => !assignedBusinessIds.has(id)
      );

      // Asignar negocios nuevos
      if (toAssign.length > 0) {
        await userService.assignBusiness(Number(id), toAssign);
      }

      // Quitar asignaciones
      for (const businessId of toUnassign) {
        await userService.removeBusinessAssignment(Number(id), businessId);
      }

      // Volver al detalle del usuario
      navigate(`/users/${id}`);

    } catch (err: any) {
      console.error('Error saving assignments:', err);
      setError(err.response?.data?.error || 'Error al guardar asignaciones');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/users/${id}`);
  };

  const filteredBusinesses = allBusinesses.filter(business =>
    (business.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (business.industry ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const changesCount = 
    Array.from(assignedBusinessIds).filter(id => !initialAssignedIds.has(id)).length +
    Array.from(initialAssignedIds).filter(id => !assignedBusinessIds.has(id)).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Typography variant="h4" component="h1">
              Asignar Negocios
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.name} - {user?.email}
            </Typography>
          </div>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || changesCount === 0}
        >
          Guardar {changesCount > 0 && `(${changesCount})`}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {assignedBusinessIds.size}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Negocios Asignados
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {allBusinesses.length - assignedBusinessIds.size}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Negocios Disponibles
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color={changesCount > 0 ? 'warning.main' : 'text.secondary'}>
              {changesCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cambios Pendientes
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar negocios por nombre o rubro..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Business List */}
      <Grid container spacing={2}>
        {filteredBusinesses.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No se encontraron negocios
              </Typography>
            </Paper>
          </Grid>
        ) : (
          filteredBusinesses.map((business) => {
            const isAssigned = assignedBusinessIds.has(business.id);
            const wasInitiallyAssigned = initialAssignedIds.has(business.id);
            const hasChanged = isAssigned !== wasInitiallyAssigned;

            return (
              <Grid item xs={12} sm={6} md={4} key={business.id}>
                <Card 
                  sx={{ 
                    border: isAssigned ? 2 : 1, 
                    borderColor: isAssigned ? 'primary.main' : 'divider',
                    bgcolor: hasChanged ? 'action.hover' : 'background.paper'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isAssigned}
                            onChange={() => handleToggleBusiness(business.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="subtitle1" fontWeight="medium">
                            {business.name}
                          </Typography>
                        }
                      />
                      {hasChanged && (
                        <Chip
                          label={isAssigned ? 'Nuevo' : 'Quitar'}
                          color={isAssigned ? 'success' : 'warning'}
                          size="small"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Rubro:</strong> {business.industry}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      <strong>Dirección:</strong> {business.address}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );
};

export default UserAssignment;
